"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const PILLARS = [
{
  number: "01",
  titleEn: "Premium Quality",
  titleZh: "優質工藝",
  descEn: "We source only Grade-A materials and employ master craftsmen with 10+ years of experience. Every finish is inspected before handover.",
  descZh: "只採用甲級材料，聘用10年以上經驗師傅，每個細節在交樓前嚴格檢查。",
  icon:
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>

},
{
  number: "02",
  titleEn: "Transparent Pricing",
  titleZh: "透明報價",
  descEn: "Detailed itemized quotations with no hidden costs. We commit to the agreed budget and deliver without surprise charges.",
  descZh: "詳細分項報價，無隱藏費用。我們承諾按預算施工，絕無額外收費。",
  icon:
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>

},
{
  number: "03",
  titleEn: "On-Time Delivery",
  titleZh: "準時交樓",
  descEn: "We maintain strict project timelines with weekly progress reports. 98% of our projects are delivered on or before the agreed date.",
  descZh: "嚴格管控工程進度，每週進度報告，98%項目準時或提前完工。",
  icon:
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>

},
{
  number: "04",
  titleEn: "Full Project Management",
  titleZh: "全程跟進",
  descEn: "Dedicated project manager from day one to final handover. Single point of contact for all coordination, permits, and contractors.",
  descZh: "由第一天到交樓，專屬項目經理全程跟進，統一協調所有工程及申請。",
  icon:
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
      </svg>

}];


export default function WhyUsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const elements = sectionRef.current?.querySelectorAll(".why-reveal");
    if (!elements) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.remove("hidden-reveal");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    elements.forEach((el) => {
      el.classList.add("hidden-reveal");
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="why-us"
      ref={sectionRef}
      className="py-24 md:py-32 relative overflow-hidden">
      
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1654826543524-f44481dcca65"
          alt="Construction workers and interior designers reviewing renovation plans on site"
          fill
          className="object-cover" />
        
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(10,22,40,0.97) 0%, rgba(26,47,94,0.92) 50%, rgba(10,22,40,0.97) 100%)" }} />
        
      </div>

      <div className="relative z-10 px-6 md:px-12 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 why-reveal reveal">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
              Why Choose Us · 為何選擇我們
            </span>
            <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
          </div>
          <h2 className="font-display section-title text-white">
            旺角裝修首選<br />
            <span className="italic" style={{ color: "#D4AF37" }}>Trusted</span> by 500+ Clients
          </h2>
        </div>

        {/* Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger-children">
          {PILLARS.map((pillar, index) =>
          <div
            key={pillar.number}
            className="why-reveal reveal p-8 rounded-2xl transition-all duration-500 group cursor-default"
            style={{
              background: "rgba(26,47,94,0.3)",
              border: "1px solid rgba(212,175,55,0.15)",
              transitionDelay: `${index * 100}ms`
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.background = "rgba(26,47,94,0.6)";
              (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(212,175,55,0.4)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.background = "rgba(26,47,94,0.3)";
              (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(212,175,55,0.15)";
            }}>
            
              <div className="flex items-start gap-5">
                <div
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.2)" }}>
                
                  {pillar.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-display text-sm font-bold" style={{ color: "rgba(212,175,55,0.5)" }}>
                      {pillar.number}
                    </span>
                    <div className="w-6 h-[1px]" style={{ background: "rgba(212,175,55,0.3)" }} />
                    <span className="text-xs uppercase tracking-widest font-bold" style={{ color: "#D4AF37" }}>
                      {pillar.titleZh}
                    </span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-3">{pillar.titleEn}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.65)" }}>
                    {pillar.descEn}
                  </p>
                  <p className="text-xs leading-relaxed mt-2" style={{ color: "rgba(245,240,232,0.45)" }}>
                    {pillar.descZh}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom image strip */}
        <div className="why-reveal reveal mt-16 grid grid-cols-3 md:grid-cols-6 gap-3 rounded-2xl overflow-hidden">
          {[
          { src: "https://images.unsplash.com/photo-1611309713808-2d273fd1e37f", alt: "Renovation in progress showing concrete walls and new flooring installation" },
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_162e3922d-1767199713989.png", alt: "Interior designer reviewing 3D rendering plans on tablet at construction site" },
          { src: "https://images.unsplash.com/photo-1665111507593-1c416f15a089", alt: "Finished luxury bathroom with marble tiles and gold fixtures" },
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1a192115e-1767098218834.png", alt: "Custom kitchen cabinetry installation with premium hardware" },
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_14e53e72b-1770299570746.png", alt: "Master bedroom with custom built-in wardrobe and mood lighting" },
          { src: "https://images.unsplash.com/photo-1624343340647-a5c0ee45e2f9", alt: "Modern office renovation with glass partitions and open workspace" }].
          map((img, i) =>
          <div key={i} className="relative h-24 md:h-32 overflow-hidden rounded-xl group">
              <AppImage
              src={img.src}
              alt={img.alt}
              fill
              className="object-cover transition-transform duration-700 group-hover:scale-110" />
            
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: "rgba(212,175,55,0.15)" }} />
            </div>
          )}
        </div>
      </div>
    </section>);

}